import { z } from 'zod';

export const MISSING_DATA_HEALTH_TYPES = {
  MISSING_ESTIMATES: 'MISSING_ESTIMATES',
  NO_TEAM_ASSIGNED: 'NO_TEAM_ASSIGNED',
  DISCONNECTED_FROM_STRATEGY: 'DISCONNECTED_FROM_STRATEGY',
} as const;

export type MissingDataHealthType =
  (typeof MISSING_DATA_HEALTH_TYPES)[keyof typeof MISSING_DATA_HEALTH_TYPES];

export const ITEM_HEALTH_RISK_TYPES = {
  PLANNED_START_DATE_PASSED: 'PLANNED_START_DATE_PASSED',
  PLANNED_FINISH_DATE_PASSED: 'PLANNED_FINISH_DATE_PASSED',
  UNPLANNED_WORK: 'UNPLANNED_WORK',
  DELTA_DIFF_LARGER_THAN_FIVE_PERCENT: 'DELTA_DIFF_LARGER_THAN_FIVE_PERCENT',
  DELTA_DIFF_LARGER_THAN_ONE_PERCENT: 'DELTA_DIFF_LARGER_THAN_ONE_PERCENT',
} as const;

export type ItemHealthRiskType =
  (typeof ITEM_HEALTH_RISK_TYPES)[keyof typeof ITEM_HEALTH_RISK_TYPES];

export const DEPENDENCY_HEALTH_TYPES = {
  BLOCKED_BY_ITEMS: 'BLOCKED_BY_ITEMS',
} as const;

export type DependencyHealthType =
  (typeof DEPENDENCY_HEALTH_TYPES)[keyof typeof DEPENDENCY_HEALTH_TYPES];

export interface BlockedItem {
  itemId: number;
  blockers: number[];
}

export type MissingDataHealthTypeMetrics = Record<MissingDataHealthType, number[]>;
export type DependencyHealthTypeMetrics = Record<DependencyHealthType, BlockedItem[]>;

export type HealthLevelCounts = Record<ItemHealthLevel, number>;

export interface HealthRiskDetail {
  healthLevel: ItemHealthLevel;
  itemIds: number[];
}

export type HealthRisks = Record<ItemHealthRiskType, HealthRiskDetail>;

export interface ItemsHealthMetrics {
  total: number;
  missingData: MissingDataHealthTypeMetrics;
  dependency: DependencyHealthTypeMetrics;
  healthLevels: HealthLevelCounts;
  healthRisks: HealthRisks;
  totalActualSeconds: number;
  totalPlanSeconds: number;
  percentageDiff: number;
  averageProgressPercentage: number;
}

export const ItemHealthLevel = {
  HEALTHY: 'HEALTHY',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  ACTION_REQUIRED: 'ACTION_REQUIRED',
  DONE: 'DONE',
} as const;

export type ItemHealthLevel = (typeof ItemHealthLevel)[keyof typeof ItemHealthLevel];

export const itemHealthLevelSchema = z.enum([
  ItemHealthLevel.HEALTHY,
  ItemHealthLevel.NEEDS_ATTENTION,
  ItemHealthLevel.ACTION_REQUIRED,
  ItemHealthLevel.DONE,
]);

export interface ItemHealthRisk {
  key: ItemHealthRiskType;
}

export interface ItemHealth {
  itemId: number;
  progress: number;
  healthLevel: ItemHealthLevel;
  isBlocked: boolean;
  aggregatedActualSeconds: number;
  aggregatedPlanSeconds: number;
  percentageDiff: number | null;
  risks: ItemHealthRisk[];
}

export interface ItemHealthByItemIdsRequest {
  itemIds: number[];
}

export interface ItemHealthByItemIdsResponse {
  data: ItemHealth[];
}
